import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, BarChart2, CheckCircle2, ArrowRight } from 'lucide-react';
import { followUps as followUpsApi, adminCommissions } from '../../services/api';

const PRIORITY_BADGE = {
  high:   'bg-danger',
  normal: 'bg-primary',
  low:    'bg-secondary',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const fmt$ = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

export default function AdminDashboard() {
  const navigate = useNavigate();

  // ── Follow-ups state ─────────────────────────────────────────────────────────
  const [dueToday,   setDueToday]   = useState([]);
  const [fuLoading,  setFuLoading]  = useState(true);
  const [completing, setCompleting] = useState({});

  // ── Commission summary state ──────────────────────────────────────────────────
  const [commSummary,  setCommSummary]  = useState(null);
  const [commLoading,  setCommLoading]  = useState(true);

  // ── Load on mount ────────────────────────────────────────────────────────────
  useEffect(() => {
    followUpsApi.dueToday()
      .then(res => setDueToday(res.data.data || []))
      .catch(() => {})
      .finally(() => setFuLoading(false));

    adminCommissions.summary()
      .then(res => {
        const rows     = res.data.data || [];
        const pending  = rows.reduce((s, r) => s + (parseFloat(r.pendingAmount)   || 0), 0);
        const incentive = rows.reduce((s, r) => s + (parseFloat(r.pendingIncentive) || 0), 0);
        setCommSummary({ pending, incentive });
      })
      .catch(() => {})
      .finally(() => setCommLoading(false));
  }, []);

  // ── Mark follow-up complete ───────────────────────────────────────────────────
  const handleComplete = async (fu) => {
    setCompleting(prev => ({ ...prev, [fu.followUpID]: true }));
    try {
      await followUpsApi.update(fu.customerID, fu.followUpID, { status: 'completed' });
      setDueToday(prev => prev.filter(f => f.followUpID !== fu.followUpID));
    } catch {
      // silently fail — the row stays visible
    } finally {
      setCompleting(prev => { const n = { ...prev }; delete n[fu.followUpID]; return n; });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid p-4">
      <h4 className="fw-bold mb-4">Dashboard</h4>

      <div className="row g-4">
        {/* ── Follow-ups Due Today ────────────────────────────────────────────── */}
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header bg-light d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2 fw-semibold">
                <Calendar size={16} />
                My Follow-Ups Due
                {dueToday.length > 0 && (
                  <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>
                    {dueToday.length}
                  </span>
                )}
              </div>
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1"
                onClick={() => navigate('/admin/crm/follow-ups')}
              >
                View All <ArrowRight size={13} />
              </button>
            </div>

            {fuLoading ? (
              <div className="card-body text-center py-4">
                <div className="spinner-border spinner-border-sm text-secondary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            ) : dueToday.length === 0 ? (
              <div className="card-body text-center text-muted py-4">
                <p className="mb-0">No follow-ups due today 🎉</p>
              </div>
            ) : (
              <ul className="list-group list-group-flush">
                {dueToday.map(fu => (
                  <li
                    key={fu.followUpID}
                    className="list-group-item d-flex align-items-center gap-2 py-2 px-3"
                  >
                    <span
                      className={`badge flex-shrink-0 text-capitalize ${PRIORITY_BADGE[fu.priority] || 'bg-secondary'}`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {fu.priority}
                    </span>
                    <div className="flex-grow-1 min-width-0">
                      <span
                        className="fw-semibold small text-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/customers/${fu.customerID}`)}
                      >
                        {fu.companyName}
                      </span>
                      <span className="small text-muted ms-1">— {fu.reminderNote}</span>
                      <span className="small text-muted ms-2">· Due: {fmtDate(fu.dueDate)}</span>
                    </div>
                    <button
                      className="btn btn-success btn-sm py-0 px-2 flex-shrink-0"
                      title="Mark complete"
                      onClick={() => handleComplete(fu)}
                      disabled={!!completing[fu.followUpID]}
                    >
                      {completing[fu.followUpID]
                        ? <span className="spinner-border" style={{ width: 12, height: 12, borderWidth: 2 }} role="status" aria-hidden="true" />
                        : <CheckCircle2 size={13} />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ── Commission Summary ───────────────────────────────────────────────── */}
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header bg-light d-flex align-items-center justify-content-between fw-semibold">
              <div className="d-flex align-items-center gap-2">
                <BarChart2 size={16} />
                My Commissions
              </div>
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1"
                onClick={() => navigate('/admin/reports')}
              >
                View Details <ArrowRight size={13} />
              </button>
            </div>

            <div className="card-body">
              {commLoading ? (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </div>
              ) : !commSummary ? (
                <p className="text-muted small mb-0">Could not load commission data.</p>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small">Pending Commissions</span>
                    <span className="fw-semibold">{fmt$(commSummary.pending)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted small">Incentive Bonuses Pending</span>
                    <span className="fw-semibold">{fmt$(commSummary.incentive)}</span>
                  </div>
                  <hr className="my-0" />
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="small fw-semibold">Total Pending</span>
                    <span className="fw-bold text-success fs-5">
                      {fmt$(commSummary.pending + commSummary.incentive)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
